/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect, useMemo } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { reduxForm, reset, submit } from 'redux-form';
import PropTypes from 'prop-types';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { FORM_NAME } from './constants';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import {
	getActiveCarIdSelector,
	getCarInfoSelector,
	getCarNameSelector,
	getCarTabsSelector,
	getFormInitialValuesSelector,
	getIsAuditTabShownSelector,
	getIsCarAddFormOpenSelector,
	getIsCarEditFormOpenSelector,
	getIsCarInfoLoadingSelector,
	getIsEventTabShownSelector,
	getIsModalShownSelector,
	getIsAccreditationTabShownSelector,
	getCarTypeIdSelector,
	getCarActionsSelector,
} from './selectors';
import {
	createCarAction,
	deleteCarAction,
	getCarInfoAction,
	getLegalsAction,
	getCarTypesAction,
	getEventDataAction,
	getModesAction,
	getOwnershipsAction,
	getTrailersListAction,
	resetStateAction,
	updateCarAction,
} from './actions';
import { CarContent } from './components/car-content';
import { removeModalQuery } from '../../utils/removeModalQuery';
import {
	getCompanyIdSelector,
	getCompanyModulesSelector,
	getCompanySettingsSelector,
	getIsAvailableCarsButtonsSelector,
	getIsAvailableTransportsButtonsSelector,
} from '../profile/selectors';
import { validate } from './utils';
import { setWindowInfoAction } from '../alert-modal/actions';
import { Audit } from '../../components/audit';
import { CarEventTab } from './components/car-event-tab';
import { Accreditation } from '../../components/accreditation';
import closeModal from '../../utils/closeModal';

const CarsModalWrapper = ({
	onGetCarInfo,
	isCarInfoLoading,
	isCarEditFormOpen,
	isCarAddFormOpen,
	carInfo,
	carName,
	onResetState,
	onDeleteCar,
	isModalShown,
	activeCarId,
	onCreateCar,
	onUpdateCar,
	onGetTrailersList,
	onClearForm,
	isAvailable,
	isAvailableTransports,
	companyModules,
	companySettings,
	companyId,
	onFormSubmit,
	onSetWindowInfo,
	isAuditTabShown,
	isEventTabShown,
	onGetOwnerships,
	onGetLegals,
	onGetCarTypes,
	onGetModes,
	carTabs,
	actions,
	onGetEventData,
	isAccreditationTabShown,
	carTypeId,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'carsModalSaga', saga });
	useInjectReducer({ key: 'carsModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	const tab = query.get('tab');

	const mode = query.get('mode');

	useEffect(() => {
		if (isModalShown) {
			onGetOwnerships();
			onGetLegals();
			onGetCarTypes();
			onGetModes();
		}
	}, [isModalShown]);

	useEffect(() => {
		if (activeCarId) onGetCarInfo(activeCarId);
	}, [activeCarId]);

	useEffect(() => {
		if (isCarEditFormOpen || isCarAddFormOpen) {
			onGetTrailersList();
		}
	}, [isCarEditFormOpen, isCarAddFormOpen]);

	useEffect(() => {
		if (isEventTabShown) onGetEventData(activeCarId);
	}, [isEventTabShown]);

	const handleCarFormOpenButtonClick = mode => {
		if (activeCarId) {
			query.set('id', activeCarId);
		} else {
			query.delete('id');
		}

		if (mode === 'edit') {
			query.set('mode', 'edit');
		}

		if (mode === 'cancel') {
			onClearForm();
			query.delete('mode');
		}

		if (mode === 'cancel' && !activeCarId) {
			onResetState();
			onClearForm();
			query.delete('modal');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		closeModal(query);

		onResetState();
		onClearForm();
		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = modal => {
		if (modal === 'info') {
			query.delete('tab');
		}

		if (modal === 'audit') {
			query.set('tab', 'audit');
		}

		if (modal === 'event') {
			query.set('tab', 'event');
		}
		if (modal === 'accreditation') {
			query.set('tab', 'accreditation');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleCarDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () => onDeleteCar({ id: activeCarId, redirect: handleModalClose, query }),
			},
		});
	};

	const handleCarFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);

		if (activeCarId) {
			onUpdateCar({
				id: activeCarId,
				redirect: () => handleCarFormOpenButtonClick('cancel'),
				searchQuery: removeModalQuery(query.toString()),
				pathname,
			});
			return;
		}

		onCreateCar({
			redirect: handleModalClose,
			resetState: onResetState,
			searchQuery: removeModalQuery(query.toString()),
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleCarFormSubmit();
		}
	};

	const modalTabContent = useMemo(() => {
		if (isAuditTabShown) {
			return <Audit entityName="cars" entityId={activeCarId} />;
		}

		if (isEventTabShown) {
			return <CarEventTab />;
		}
		if (isAccreditationTabShown) {
			return <Accreditation type="cars" id={activeCarId} typeId={carTypeId} />;
		}

		return null;
	}, [isAuditTabShown, isEventTabShown, isAccreditationTabShown]);

	const tabs = useMemo(() => {
		const tabs = [
			{
				id: 'info',
				text: 'Информация',
				onClick: () => handleNavItemClick('info'),
				isActive: !tab && !mode,
			},
		];

		if (carTabs.audit) {
			tabs.push({
				id: 'audit',
				text: 'Изменения',
				onClick: () => handleNavItemClick('audit'),
				isActive: isAuditTabShown && !mode,
			});
		}

		if (carTabs.event) {
			tabs.push({
				id: 'event',
				text: 'События',
				onClick: () => handleNavItemClick('event'),
				isActive: isEventTabShown && !mode,
			});
		}

		if (carTabs.accreditation) {
			tabs.push({
				id: 'accreditation',
				text: 'Аккредитация',
				onClick: () => handleNavItemClick('accreditation'),
				isActive: isAccreditationTabShown && !mode,
			});
		}

		return tabs;
	}, [carTabs, tab, mode, isAuditTabShown, isEventTabShown, isAccreditationTabShown]);

	const handleCarApprove = () => {
		if (companyId !== carInfo[0].value.id && companyModules?.transports && !companySettings?.transports) {
			const query = new URLSearchParams(search);
			query.set('modal', 'select_accreditation');
			history.push(`${pathname}?${query}`);
		} else if (
			companyId === carInfo[0].value.id &&
			companyModules?.my_transports &&
			!companySettings?.my_transports
		) {
			const query = new URLSearchParams(search);
			query.set('modal', 'select_accreditation');
			history.push(`${pathname}?${query}`);
		} else {
			onSetWindowInfo({
				type: 'forbidden',
				title: 'Внимание',
				text: 'Для получения доступа к функционалу, обратитесь, пожалуйста, к администратору.',
				phone: '+7 (495) 136-22-00',
				email: 'hello@mak-logic.ru',
			});
		}
	};

	return (
		<ModalWindow
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={
				isCarEditFormOpen || isCarAddFormOpen
					? `${isCarEditFormOpen ? 'Изменить машину' : 'Создать машину'}`
					: carName
			}
			navItems={isCarAddFormOpen || isCarEditFormOpen ? [] : tabs}
			modalContent={
				!tab ? (
					<CarContent
						carInfo={carInfo}
						isCarInfoLoading={isCarInfoLoading}
						isCarFormOpen={isCarEditFormOpen || isCarAddFormOpen}
					/>
				) : (
					modalTabContent
				)
			}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'car' }]}
			footerButtons={
				<ModalFooterButtons
					leftButtons={
						!(isCarEditFormOpen || isCarAddFormOpen) && isAvailable.delete && actions.delete && !tab
							? [
									{
										onClick: handleCarDelete,
										text: 'Удалить',
										color: 'warning',
										disabled: isCarInfoLoading,
									},
							  ]
							: []
					}
					rightButtons={
						isCarEditFormOpen || isCarAddFormOpen
							? [
									{
										onClick: handleCarFormSubmit,
										text: 'Сохранить',
										color: 'primary',
										loading: isCarInfoLoading,
										disabled: isCarInfoLoading,
									},
									{
										onClick: () => handleCarFormOpenButtonClick('cancel'),
										text: 'Отмена',
										color: 'secondary',
										disabled: isCarInfoLoading,
									},
							  ]
							: [
									...(isAvailable.update && actions.update && !tab
										? [
												{
													onClick: () => handleCarFormOpenButtonClick('edit'),
													text: 'Изменить',
													color: 'primary',
													disabled: isCarInfoLoading,
												},
										  ]
										: []),
									...(isAvailableTransports.update && actions.approve && !tab
										? [
												{
													onClick: handleCarApprove,
													text: 'Одобрить',
													color: 'success',
													disabled: isCarInfoLoading,
												},
										  ]
										: []),
									{
										onClick: handleModalClose,
										text: 'Закрыть',
										color: 'secondary',
									},
							  ]
					}
				/>
			}
		/>
	);
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(CarsModalWrapper);

const mapStateToProps = createStructuredSelector({
	carInfo: getCarInfoSelector(),
	isCarInfoLoading: getIsCarInfoLoadingSelector(),
	isCarAddFormOpen: getIsCarAddFormOpenSelector(),
	isCarEditFormOpen: getIsCarEditFormOpenSelector(),
	carName: getCarNameSelector(),
	isModalShown: getIsModalShownSelector(),
	activeCarId: getActiveCarIdSelector(),
	initialValues: getFormInitialValuesSelector(),
	isAvailable: getIsAvailableCarsButtonsSelector(),
	isAvailableTransports: getIsAvailableTransportsButtonsSelector(),
	companySettings: getCompanySettingsSelector(),
	companyModules: getCompanyModulesSelector(),
	companyId: getCompanyIdSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isEventTabShown: getIsEventTabShownSelector(),
	carTabs: getCarTabsSelector(),
	isAccreditationTabShown: getIsAccreditationTabShownSelector(),
	carTypeId: getCarTypeIdSelector(),
	actions: getCarActionsSelector(),
});

const mapDispatchToProps = {
	onGetCarInfo: getCarInfoAction,
	onDeleteCar: deleteCarAction,
	onResetState: resetStateAction,
	onCreateCar: createCarAction,
	onUpdateCar: updateCarAction,
	onGetTrailersList: getTrailersListAction,
	onClearForm: () => reset(FORM_NAME),
	onFormSubmit: submit,
	onSetWindowInfo: setWindowInfoAction,
	onGetOwnerships: getOwnershipsAction,
	onGetLegals: getLegalsAction,
	onGetCarTypes: getCarTypesAction,
	onGetModes: getModesAction,
	onGetEventData: getEventDataAction,
};

const CarsModal = connect(mapStateToProps, mapDispatchToProps)(withForm);

CarsModalWrapper.propTypes = {
	onGetCarInfo: PropTypes.func.isRequired,
	carInfo: PropTypes.array.isRequired,
	isCarInfoLoading: PropTypes.bool.isRequired,
	isCarEditFormOpen: PropTypes.bool.isRequired,
	isCarAddFormOpen: PropTypes.bool.isRequired,
	carName: PropTypes.string.isRequired,
	onDeleteCar: PropTypes.func.isRequired,
	onResetState: PropTypes.func.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	activeCarId: PropTypes.string,
	onCreateCar: PropTypes.func.isRequired,
	onUpdateCar: PropTypes.func.isRequired,
	onGetTrailersList: PropTypes.func.isRequired,
	onClearForm: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isAvailableTransports: PropTypes.objectOf(PropTypes.bool),
	companySettings: PropTypes.objectOf(PropTypes.bool),
	companyModules: PropTypes.objectOf(PropTypes.bool),
	companyId: PropTypes.number,
	onFormSubmit: PropTypes.func.isRequired,
	onSetWindowInfo: PropTypes.func.isRequired,
	isAuditTabShown: PropTypes.bool.isRequired,
	isEventTabShown: PropTypes.bool.isRequired,
	isAccreditationTabShown: PropTypes.bool.isRequired,
	onGetOwnerships: PropTypes.func.isRequired,
	onGetLegals: PropTypes.func.isRequired,
	onGetCarTypes: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	carTabs: PropTypes.shape({
		audit: PropTypes.bool,
		event: PropTypes.bool,
		accreditation: PropTypes.bool,
	}).isRequired,
	actions: PropTypes.objectOf(PropTypes.bool).isRequired,
	onGetEventData: PropTypes.func.isRequired,
	carTypeId: PropTypes.number,
};

export default CarsModal;
